import { inject, Injectable, Provider } from '@angular/core';

import { map, Observable } from 'rxjs';

import { UserTheme } from '@bn/primitives';
import { THEME_PERSISTENCE, ThemePersistence } from '@bn/web-ui/theme';

import { UserSettingsApiService } from '../../user/services';

/**
 * Backs web-ui's ThemeService with the account-level theme from `/v2/user/settings`.
 *
 * ThemeService cannot depend on UserSettingsApiService directly — that would put
 * HttpClient and auth below the design system. This adapter lives in `features`,
 * which may legally depend on both.
 */
@Injectable({ providedIn: 'root' })
export class UserSettingsThemePersistence implements ThemePersistence {
  readonly #userSettingsApi = inject(UserSettingsApiService);

  loadTheme$(): Observable<UserTheme> {
    return this.#userSettingsApi.getUserSettings$().pipe(map((userSettings) => userSettings?.theme ?? UserTheme.light));
  }

  saveTheme$(theme: UserTheme): Observable<unknown> {
    // Send ONLY the changed key. Reading the settings first and spreading them back
    // means a stale snapshot can overwrite saves that landed in between (favourites,
    // dashboard widgets, …) — the API merges partial payloads server-side.
    return this.#userSettingsApi.setUserSettings({ theme });
  }
}

/** Opt an app into persisting the theme against the user's account. */
export const provideUserSettingsTheme = (): Provider => ({
  provide: THEME_PERSISTENCE,
  useExisting: UserSettingsThemePersistence,
});
